import {
	Alert,
	Box,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import type {
	BarrelAssignment,
	BarrelMatch,
	BarrelMatchSummary,
} from "./models";

function assignmentKey(assignment: BarrelAssignment) {
	return JSON.stringify([
		assignment.rollshopId ?? assignment.rollshopName,
		assignment.mesId ?? assignment.mesName,
	]);
}

function assignmentsFor(matches: BarrelMatch[]) {
	const assignments = new Map<string, BarrelAssignment>();
	for (const match of matches) {
		for (const assignment of match.assignments ?? [
			{
				rollshopName: match.rollshopName,
				rollshopId: null,
				mesName: match.mesName,
				mesId: null,
				rollsetCount: match.rollsetCount,
			},
		]) {
			assignments.set(assignmentKey(assignment), assignment);
		}
	}
	return [...assignments.values()];
}

function relationshipSize(assignments: BarrelAssignment[]) {
	const rollshopCount = new Set(
		assignments.map((a) => a.rollshopId ?? a.rollshopName),
	).size;
	const mesCount = new Set(assignments.map((a) => a.mesId ?? a.mesName)).size;
	return `${rollshopCount} Rollshop ${rollshopCount === 1 ? "barrel" : "barrels"} → ${mesCount} MES ${mesCount === 1 ? "barrel" : "barrels"}`;
}

function BarrelName({ name, id }: { name: string; id: string | null }) {
	return (
		<>
			{name}
			{id != null && (
				<Typography component="span" variant="caption" color="text.secondary">
					{" "}
					(ID {id})
				</Typography>
			)}
		</>
	);
}

function BarrelAssignmentTable({
	assignments,
}: {
	assignments: BarrelAssignment[];
}) {
	return (
		<Box sx={{ maxHeight: 260, overflow: "auto", my: 1 }}>
			<Table
				size="small"
				stickyHeader
				aria-label="Barrel relationships through common rollsets"
			>
				<TableHead>
					<TableRow>
						<TableCell>Rollshop barrel</TableCell>
						<TableCell>MES barrel</TableCell>
						<TableCell align="right">Common rollsets</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{assignments.map((assignment) => (
						<TableRow key={assignmentKey(assignment)}>
							<TableCell sx={{ overflowWrap: "anywhere" }}>
								<BarrelName
									name={assignment.rollshopName}
									id={assignment.rollshopId}
								/>
							</TableCell>
							<TableCell sx={{ overflowWrap: "anywhere" }}>
								<BarrelName name={assignment.mesName} id={assignment.mesId} />
							</TableCell>
							<TableCell align="right">{assignment.rollsetCount}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</Box>
	);
}

export function PendingBarrelSummary({ matches }: { matches: BarrelMatch[] }) {
	const assignments = assignmentsFor(matches);
	const byMes = new Map<string, BarrelAssignment[]>();
	for (const assignment of assignments) {
		const key = assignment.mesId ?? assignment.mesName;
		const sources = byMes.get(key);
		if (sources) sources.push(assignment);
		else byMes.set(key, [assignment]);
	}
	return (
		<>
			<Typography variant="body2" color="info.main">
				{relationshipSize(assignments)}
			</Typography>
			{[...byMes].map(([key, sources]) => (
				<Box key={key} sx={{ mt: 0.5, overflowWrap: "anywhere" }}>
					<Typography variant="body2">
						MES: <BarrelName name={sources[0].mesName} id={sources[0].mesId} />
					</Typography>
					<Typography variant="body2">Rollshop keys:</Typography>
					<Box component="ul" sx={{ my: 0, pl: 2 }}>
						{sources.map((source) => (
							<Typography
								component="li"
								variant="body2"
								key={assignmentKey(source)}
							>
								<BarrelName name={source.rollshopName} id={source.rollshopId} />
							</Typography>
						))}
					</Box>
				</Box>
			))}
		</>
	);
}

export function PendingBarrelMatches({ matches }: { matches: BarrelMatch[] }) {
	const assignments = assignmentsFor(matches);
	return (
		<Box mb={2}>
			<Alert severity="info">
				Match pending: {relationshipSize(assignments)}. These links come from
				common rollset assignments.
			</Alert>
			<BarrelAssignmentTable assignments={assignments} />
			{[
				...new Set(
					matches
						.filter(
							(match) => match.rollshopTargets === 1 && match.mesSources === 1,
						)
						.map((match) => match.reason),
				),
			].map((reason) => (
				<Typography key={reason} variant="body2" sx={{ mb: 0.5 }}>
					{reason}
				</Typography>
			))}
			<Typography variant="body2" color="text.secondary">
				No match has been confirmed. Review these relationships before inserting
				a new barrel in MES.
			</Typography>
		</Box>
	);
}

export function BarrelMatchingSummary({
	summary,
}: {
	summary: BarrelMatchSummary;
}) {
	return (
		<Alert severity="info" sx={{ mb: 2 }}>
			{summary.consistent} consistent relationships · {summary.possible}{" "}
			possible (single rollset) · {summary.ambiguous} ambiguous. Only consistent
			relationships are added to the matched pairs.
			<Typography variant="body2" mt={0.5}>
				{summary.resolvedRollsets} of {summary.commonRollsets} common rollsets
				resolve a barrel on both sides. {summary.missingMesBarrel} have no MES
				barrel; {summary.unresolvedRollshopBarrel} have an unresolved Rollshop
				barrel; {summary.invalidMesBarrel} have an invalid MES barrel reference.
				{summary.ignoredRollsets > 0 &&
					` ${summary.ignoredRollsets} rollset records cannot be uniquely paired.`}
			</Typography>
			<Typography variant="body2" mt={0.5}>
				Relationships are recalculated on each run. MES names are preserved.
				This category overlaps with value differences and unmatched records.
			</Typography>
		</Alert>
	);
}

export function BarrelMatchStatus({ match }: { match: BarrelMatch }) {
	return (
		<Chip
			size="small"
			label={
				match.status === "Consistent"
					? "Consistent relationship"
					: `${match.status} relationship`
			}
			color={match.accepted ? "info" : "warning"}
		/>
	);
}

export function BarrelEvidence({ match }: { match: BarrelMatch }) {
	return (
		<Box mb={2}>
			<Alert severity={match.accepted ? "info" : "warning"} sx={{ mb: 1 }}>
				{match.reason}
			</Alert>
			<Typography variant="body2">
				<strong>Rollshop:</strong> {match.rollshopName}
			</Typography>
			<Typography variant="body2">
				<strong>MES:</strong> {match.mesName}
			</Typography>
			{!match.accepted && (
				<BarrelAssignmentTable assignments={assignmentsFor([match])} />
			)}
			<Typography variant="subtitle2" mt={1}>
				Supporting rollsets ({match.rollsets.length} of {match.rollsetCount})
			</Typography>
			<Box sx={{ maxHeight: 220, overflow: "auto" }}>
				<Table size="small" stickyHeader aria-label="Supporting rollsets">
					<TableHead>
						<TableRow>
							<TableCell>Rollshop rollset</TableCell>
							<TableCell>MES rollset</TableCell>
							<TableCell>Rollshop barrel reference</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{match.rollsets.map((rollset) => (
							<TableRow key={`${rollset.rollshop}/${rollset.mes}`}>
								<TableCell>{rollset.rollshop}</TableCell>
								<TableCell>{rollset.mes}</TableCell>
								<TableCell>{rollset.resolution}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</Box>
		</Box>
	);
}
